import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nProvider";
import ProductCard from "@/components/ProductCard";
import CategoryTabs from "@/components/CategoryTabs";
import OrnamentDivider from "@/components/OrnamentDivider";
import { buildCategories, filterByCategory } from "@/utils/categories";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductGrid() {
  const { t, lang } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");

  // Listen for external category-set requests (e.g. from WM banner CTA)
  useEffect(() => {
    const handler = (e) => {
      const key = e?.detail;
      if (typeof key === "string") setActiveCat(key);
    };
    window.addEventListener("kh:set-category", handler);
    return () => window.removeEventListener("kh:set-category", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/products?limit=50`);
        if (!cancelled) setProducts(res.data.products || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => buildCategories(products, lang), [products, lang]);
  const filtered = useMemo(
    () => filterByCategory(products, categories, activeCat),
    [products, categories, activeCat]
  );

  return (
    <section
      id="shop"
      data-testid="product-grid-section"
      className="relative py-28 sm:py-36 bg-[#050505] kh-section-cinematic"
    >
      {/* Ambient burgundy glow top-left */}
      <div className="absolute top-0 left-0 w-1/2 h-96 pointer-events-none opacity-30"
        style={{ background: "radial-gradient(ellipse at top left, rgba(123, 30, 46, 0.4), transparent 60%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        {/* Cinematic chapter header */}
        <div className="mb-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#A93043]" />
              <span className="kh-chapter">{t.products.chapter}</span>
              <span className="h-px w-8 bg-[#A93043]" />
            </div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-12 bg-[#D4AF37]" />
              <span className="kh-eyebrow">{t.products.eyebrow}</span>
            </div>
            <h2
              data-testid="product-grid-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9]"
            >
              {t.products.title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="font-serif-luxury italic text-[#D4AF37] normal-case tracking-normal">
                {t.products.title.split(" ").slice(-1)[0].toLowerCase()}
              </span>
            </h2>
            <p className="mt-6 font-body text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              {t.products.subtitle}
            </p>
          </div>

          {/* Right side: cinematic stats column */}
          <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-4">
            <div className="flex items-baseline gap-3">
              <span className="font-serif-luxury italic text-[#B5651D] text-xl">No.</span>
              <span className="font-display text-7xl lg:text-8xl text-[#D4AF37] leading-none">
                {String(filtered.length).padStart(2, "0")}
              </span>
            </div>
            <span className="font-body text-[10px] tracking-[0.35em] uppercase text-white/40">
              {activeCat && activeCat !== "all"
                ? (categories.find((c) => c.key === activeCat)?.label || "") + " · pieces"
                : "Pieces in rotation"}
            </span>
          </div>
        </div>

        {/* Category tabs */}
        {!loading && !error && products.length > 0 && (
          <CategoryTabs
            categories={categories}
            activeKey={activeCat}
            onChange={setActiveCat}
            totalCount={products.length}
          />
        )}

        {/* Loading */}
        {loading && (
          <div data-testid="products-loading" className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            <p className="font-body text-sm tracking-[0.2em] uppercase text-white/50">
              {t.products.loading}
            </p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div data-testid="products-error" className="border border-[#1f1f29] p-12 text-center">
            <p className="font-body text-white/60">{t.products.empty}</p>
            <p className="mt-3 font-body text-xs text-white/30 tracking-wider uppercase">
              {String(error).slice(0, 120)}
            </p>
          </div>
        )}

        {/* Empty state - whole shop */}
        {!loading && !error && products.length === 0 && (
          <div data-testid="products-empty" className="kh-belt-frame border border-[#1f1f29] p-16 text-center">
            <p className="font-display text-4xl uppercase text-white mb-2 tracking-wider">
              Coming <span className="font-serif-luxury italic text-[#D4AF37]">soon</span>
            </p>
            <p className="font-body text-white/55 max-w-md mx-auto">
              {t.products.empty}
            </p>
          </div>
        )}

        {/* Empty state — current category */}
        {!loading && !error && products.length > 0 && filtered.length === 0 && (
          <div data-testid="category-empty" className="border border-[#1f1f29] p-12 text-center">
            <p className="font-display text-2xl uppercase text-white tracking-wider mb-2">
              {t.products.categoryEmpty}
            </p>
            <button
              onClick={() => setActiveCat("all")}
              className="mt-3 font-body text-xs tracking-[0.25em] uppercase text-[#D4AF37] hover:text-white transition-colors"
            >
              ← {t.products.showAll}
            </button>
          </div>
        )}

        {/* Products grid */}
        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="products-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {filtered.map((p, i) => (
              <div
                key={p.id}
                style={{ animationDelay: `${i * 0.06}s` }}
                className="kh-fade-up"
              >
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom ornament divider */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 mt-20">
        <OrnamentDivider />
      </div>
    </section>
  );
}
